import {makePropsAreEqual} from '@wandb/weave/common/util/shouldUpdate';
import {clsx} from 'clsx';
import React, {type FC, memo, useCallback, useMemo, useState} from 'react';
import Measure from 'react-measure';

import type * as ReportsTypes from '../../../state/reports/types';
import * as ViewHooks from '../../../state/views/hooks';
import {usePanelBankSectionConfig} from '../../../state/views/panelBankSectionConfig/hooks';
import type * as PanelSettingsViewTypes from '../../../state/views/panelSettings/types';
import type * as SectionViewTypes from '../../../state/views/section/types';
import {getSectionsWithVisiblePanels} from '../../../util/panelbank';
import {keyExistsInQueryString} from '../../../util/query-string';
import {CreateReportModal} from '../../CreateReportModal';
import {
  KeyInfoQueryContextProvider,
  useKeyInfoQueryContext,
} from '../../MultiRunWorkspace/KeyInfoQueryContext';
import {type OrganizedSettings} from '../../PanelBank/types';
import {RunSets} from '../../RunSets';
import {PanelsSharedRefsContextProvider} from '../../Views/contexts/PanelsSharedRefsContext';
import {useReportSectionProps} from '../hooks';
import {ReportGridLayout} from '../ReportGridLayout';
import {IMPORT_PANEL_QS, REPORT_PANEL_GRID_MENU} from '../types';
import {ReportPanelGridActions} from './ReportPanelGridActions';
import * as S from './ReportPanelGridSection.styles';

export type ReportPanelGridSectionProps = {
  entityName: string;
  projectName: string;
  panelSettingsRef: PanelSettingsViewTypes.Ref;
  sectionRef: SectionViewTypes.Ref;
  viewRef: ReportsTypes.ReportViewRef;
  /**
   * This determines if report is published or not.
   */
  readOnly?: boolean;
};

const ReportPanelGridSectionComp: FC<ReportPanelGridSectionProps> = props => {
  const {
    entityName,
    projectName,
    panelSettingsRef,
    sectionRef,
    viewRef,
    readOnly,
  } = props;
  const {sectionPart} = useReportSectionProps(sectionRef);

  const [panelBankWidth, setPanelBankWidth] = useState(0);

  // make sure this is well memoized or the panel components will rerender every update
  // reports has not been migrated to use the new settings structure yet
  const panelSettings = ViewHooks.usePart(panelSettingsRef);
  const inheritedSettings: OrganizedSettings = useMemo(() => {
    return {
      linePlot: panelSettings,
    };
  }, [panelSettings]);

  const useExportableSections = () => {
    const panelBankSectionConfig = usePanelBankSectionConfig(
      sectionPart.panelBankSectionConfigRef
    );
    return useMemo(
      () => getSectionsWithVisiblePanels([panelBankSectionConfig]),
      [panelBankSectionConfig]
    );
  };

  // To control the grid panel menu popups
  const initialOpenedMenu = useMemo(
    () =>
      keyExistsInQueryString(IMPORT_PANEL_QS)
        ? REPORT_PANEL_GRID_MENU.IMPORT_PANEL
        : undefined,
    []
  );
  const [openedMenu, setOpenedMenu] = useState<
    REPORT_PANEL_GRID_MENU | undefined
  >(initialOpenedMenu);

  const onClickGrid = useCallback(() => {
    if (openedMenu !== REPORT_PANEL_GRID_MENU.ADD_PANEL) {
      // HAX: we need the setTimeout to ensure this runs
      // after the add vis Popup's onClose handler
      setTimeout(() => setOpenedMenu(REPORT_PANEL_GRID_MENU.ADD_PANEL));
    }
  }, [openedMenu]);

  // Make a history keys query so the state is in redux.
  // This allows query merging for panels in the section.
  const {keyInfoQuery} = useKeyInfoQueryContext();
  if (keyInfoQuery.error) {
    return (
      <div className="report-section">
        <S.EmptyReportSection>
          This set of panels contains runs from a private project, which cannot
          be shown in this report
        </S.EmptyReportSection>
      </div>
    );
  }

  return (
    <div
      className={clsx('report-section', 'panel-bank')}
      data-test="report-panel-grid-section">
      <Measure
        bounds
        onResize={contentRect => {
          setPanelBankWidth(contentRect.bounds ? contentRect.bounds.width : 0);
        }}>
        {({measureRef}) => (
          <>
            <div className="grid-section-wrapper">
              <div ref={measureRef}>
                {panelBankWidth > 0 && (
                  <ReportGridLayout
                    inheritedSettings={inheritedSettings}
                    onClickGrid={onClickGrid}
                    panelBankConfigRef={sectionPart.panelBankConfigRef}
                    panelBankSectionConfigRef={
                      sectionPart.panelBankSectionConfigRef
                    }
                    panelBankWidth={panelBankWidth}
                    readOnly={readOnly}
                  />
                )}
              </div>
            </div>
            {!readOnly && (
              <ReportPanelGridActions
                entityName={entityName}
                projectName={projectName}
                sectionPart={sectionPart}
                viewRef={viewRef}
                openedMenu={openedMenu}
                setOpenedMenu={setOpenedMenu}
              />
            )}
            {!(readOnly && sectionPart.hideRunSets) && (
              <RunSets sectionRef={sectionRef} readOnly={readOnly} />
            )}
          </>
        )}
      </Measure>
      <CreateReportModal
        useExportableSections={useExportableSections}
        inheritedSettings={inheritedSettings}
      />
    </div>
  );
};

export const ReportPanelGridSection = memo(
  (props: ReportPanelGridSectionProps) => {
    const {sectionPart} = useReportSectionProps(props.sectionRef);
    return (
      <KeyInfoQueryContextProvider
        runSetRefs={sectionPart.runSetRefs}
        customRunNamesRef={sectionPart.customRunNamesRef}
        allowMultiRunsetHistoryKeys={true}>
        <PanelsSharedRefsContextProvider
          customRunColorsRef={sectionPart.customRunColorsRef}
          customRunNamesRef={sectionPart.customRunNamesRef}
          semanticLegendRef={sectionPart.semanticLegendRef}
          runSetRefs={sectionPart.runSetRefs}>
          <ReportPanelGridSectionComp {...props} />
        </PanelsSharedRefsContextProvider>
      </KeyInfoQueryContextProvider>
    );
  },
  makePropsAreEqual({
    name: 'ReportPanelGridSection',
    deep: ['config', 'customRunColors', 'panelSettings', 'queries'],
    ignoreFunctions: false,
    debug: false,
    verbose: true,
  })
);
